import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import ENDPOINTS from '../../../Constants/ApiEndpoints';
import './MedicineBatchDetailModal.css';
import { FaTimes, FaBolt, FaExclamationTriangle, FaBoxes } from 'react-icons/fa';

const FORBIDDEN_CHAR_REGEX = /[,\\'"]/;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MedicineBatchDetailModal = ({
  isOpen,
  onClose,
  medicine,
  onConfirm
}) => {
  // Form state initialized from selected medicine / last entry
  const [formData, setFormData] = useState({
    // Non-editable or editable item metadata
    name: '',
    extension: '',
    itemType: 'TABLET',
    packType: 'strip',
    category: 'MEDICINES',
    scheduleType: 'Schedule H',
    composition: '',
    manufacturer: '',
    hsn: '',
    gstCategory: '12',
    isEditingGst: false,

    // Thresholds & Rack
    rack: '',
    thresholdQuantity: '10',
    fastThresholdValue: '20',
    slowThresholdValue: '10',

    // Current Batch Details
    packQuantity: '10',
    noOfPackets: '1',
    freePackets: '0',
    batchNumber: '',
    expiryMonth: 'February',
    expiryYear: '2029',
    packRate: '',
    packMrp: '',
    packMrpDiscount: '0',
    discountPercentage: '0',

    // Badges
    weeklyVelocity: '73',
    stockBalance: '0'
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [existingBatches, setExistingBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('NEW');
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const isExistingBatch = Boolean(selectedBatchId && selectedBatchId !== 'NEW');

  // Years list for dropdown (current year to +15 years)
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const list = [];
    for (let i = currentYear; i <= currentYear + 15; i++) {
      list.push(String(i));
    }
    return list;
  }, []);

  useEffect(() => {
    if (medicine && isOpen) {
      // Parse expiry date if present
      let expMonth = 'February';
      let expYear = '2029';
      if (medicine.expiryDate) {
        const d = new Date(medicine.expiryDate);
        if (!isNaN(d.getTime())) {
          expMonth = MONTHS[d.getMonth()];
          expYear = String(d.getFullYear());
        }
      }

      setFormData({
        name: medicine.brand || medicine.rawName || medicine.itemName || '',
        extension: medicine.extension || '',
        itemType: medicine.itemType || 'TABLET',
        packType: (medicine.packType || 'strip').toLowerCase(),
        category: medicine.category || 'MEDICINES',
        scheduleType: medicine.scheduleType || 'Schedule H',
        composition: medicine.composition || 'DICYCLOMINE (10MG) + MEFENAMIC ACID (250MG)',
        manufacturer: medicine.brand || medicine.manufacturer || 'BLUE CROSS',
        hsn: medicine.hsn || medicine.hsnCode || '30049039',
        gstCategory: String(medicine.gstPercent || '12'),
        isEditingGst: false,

        rack: medicine.rack || '',
        thresholdQuantity: String(medicine.thresholdQuantity || '10'),
        fastThresholdValue: String(medicine.fastThresholdValue || '20'),
        slowThresholdValue: String(medicine.slowThresholdValue || '10'),

        packQuantity: String(medicine.packQuantity || '10'),
        noOfPackets: String(medicine.quantity || '1'),
        freePackets: String(medicine.freeQuantity || '0'),
        batchNumber: medicine.batchNo || '',
        expiryMonth: expMonth,
        expiryYear: expYear,
        packRate: String(medicine.packRate != null ? medicine.packRate : (medicine.rate || '')),
        packMrp: String(medicine.packMrp != null ? medicine.packMrp : (medicine.mrp || '')),
        packMrpDiscount: String(medicine.packMrpDiscount || '0'),
        packMrpDiscountType: medicine.packMrpDiscountType || '%',
        discountPercentage: String(medicine.discountPercent != null ? medicine.discountPercent : (medicine.discountValue || '0')),
        supplierDiscountType: medicine.discountType || medicine.supplierDiscountType || '%',

        weeklyVelocity: medicine.weeklyVelocity || '73',
        stockBalance: String(
          medicine.stockQuantity != null
            ? medicine.stockQuantity
            : (medicine.availableUnits != null
                ? medicine.availableUnits
                : (medicine.availableQuantity != null
                    ? medicine.availableQuantity
                    : (medicine.stockBalance != null ? medicine.stockBalance : '0')))
        )
      });
      setFieldErrors({});
      setErrorMessage('');

      // Fetch batch ledger truth for this medicine from server
      const medId = medicine.medicineId || medicine.id;
      if (medId) {
        setIsLoadingBatches(true);
        axios.get(ENDPOINTS.GET_MEDICINE_BATCHES(medId))
          .then((res) => {
            const batches = Array.isArray(res.data) ? res.data : [];
            setExistingBatches(batches);
            if (medicine.batchNo) {
              const matched = batches.find(
                (b) => b.batchNumber && b.batchNumber.toLowerCase() === String(medicine.batchNo).toLowerCase()
              );
              if (matched) {
                setSelectedBatchId(String(matched.id));
              } else {
                setSelectedBatchId('NEW');
              }
            } else {
              setSelectedBatchId('NEW');
            }
          })
          .catch((err) => {
            console.warn('Could not load batches for medicine id ' + medId, err);
            setExistingBatches([]);
          })
          .finally(() => {
            setIsLoadingBatches(false);
          });
      } else {
        setExistingBatches([]);
        setSelectedBatchId('NEW');
      }
    }
  }, [medicine, isOpen]);

  const handleBatchSelect = (batchIdVal) => {
    setSelectedBatchId(batchIdVal);
    if (batchIdVal === 'NEW') {
      setFormData((prev) => ({
        ...prev,
        batchNumber: ''
      }));
      return;
    }
    const chosenBatch = existingBatches.find((b) => String(b.id) === String(batchIdVal));
    if (chosenBatch) {
      let expMonth = 'February';
      let expYear = '2029';
      if (chosenBatch.expiryDate) {
        const d = new Date(chosenBatch.expiryDate);
        if (!isNaN(d.getTime())) {
          expMonth = MONTHS[d.getMonth()];
          expYear = String(d.getFullYear());
        }
      }
      setFormData((prev) => ({
        ...prev,
        batchNumber: chosenBatch.batchNumber || prev.batchNumber,
        expiryMonth: expMonth,
        expiryYear: expYear,
        packRate: chosenBatch.rate != null && Number(chosenBatch.rate) > 0 ? String(chosenBatch.rate) : prev.packRate,
        packMrp: chosenBatch.mrp != null && Number(chosenBatch.mrp) > 0 ? String(chosenBatch.mrp) : prev.packMrp,
        rack: chosenBatch.rack || prev.rack,
        packQuantity: chosenBatch.packQuantity != null ? String(chosenBatch.packQuantity) : prev.packQuantity,
        stockBalance: String(chosenBatch.stockQuantity != null ? chosenBatch.stockQuantity : '0')
      }));
    }
  };

  // ─── Live Calculations for Selling MRP, MRP/Unit, and Total Amount ────────
  const liveCalculations = useMemo(() => {
    const packQty = Math.max(1, Number(formData.packQuantity) || 1);
    const noOfPacks = Number(formData.noOfPackets) || 0;
    const freePacks = Number(formData.freePackets) || 0;

    // Pack MRP & Discount on MRP (% or ₹)
    const grossPackMrp = Number(formData.packMrp) || 0;
    const mrpDiscVal = Number(formData.packMrpDiscount) || 0;
    const isMrpDiscPercent = (formData.packMrpDiscountType || '%') === '%';
    const mrpDiscountAmount = isMrpDiscPercent ? (grossPackMrp * mrpDiscVal) / 100 : mrpDiscVal;
    const netPackMrp = Math.max(0, grossPackMrp - mrpDiscountAmount);

    // MRP per Unit (calculated from Selling MRP per pack / pack quantity)
    const mrpPerUnit = packQty > 0 ? (netPackMrp / packQty) : netPackMrp;
    const origMrpPerUnit = packQty > 0 ? (grossPackMrp / packQty) : grossPackMrp;

    // Pack Rate & Supplier Discount (% or ₹)
    const packRate = Number(formData.packRate) || 0;
    const unitRate = packQty > 0 ? (packRate / packQty) : packRate;
    const basePurchaseAmount = noOfPacks * packRate;

    const suppDiscVal = Number(formData.discountPercentage) || 0;
    const isSuppDiscPercent = (formData.supplierDiscountType || '%') === '%';
    let supplierDiscountAmount = 0;
    let suppDiscPercent = 0;

    if (isSuppDiscPercent) {
      suppDiscPercent = suppDiscVal;
      supplierDiscountAmount = (basePurchaseAmount * suppDiscVal) / 100;
    } else {
      supplierDiscountAmount = Math.min(basePurchaseAmount, noOfPacks * suppDiscVal);
      suppDiscPercent = basePurchaseAmount > 0 ? (supplierDiscountAmount / basePurchaseAmount) * 100 : 0;
    }

    const taxableAmount = Math.max(0, basePurchaseAmount - supplierDiscountAmount);
    const gstRate = Number(formData.gstCategory) || 0;
    const gstAmount = (taxableAmount * gstRate) / 100;
    const totalAmount = taxableAmount + gstAmount;

    // Net unit purchase rate considering quantity + free
    const totalUnits = (noOfPacks + freePacks) * packQty;
    const effectiveNetUnitRate = totalUnits > 0 ? (basePurchaseAmount / totalUnits) : unitRate;

    return {
      packQty,
      grossPackMrp,
      mrpDiscountAmount,
      netPackMrp,
      mrpPerUnit,
      origMrpPerUnit,
      packRate,
      unitRate,
      basePurchaseAmount,
      supplierDiscountAmount,
      suppDiscPercent,
      taxableAmount,
      gstRate,
      gstAmount,
      totalAmount,
      effectiveNetUnitRate
    };
  }, [
    formData.packQuantity,
    formData.noOfPackets,
    formData.freePackets,
    formData.packMrp,
    formData.packMrpDiscount,
    formData.packMrpDiscountType,
    formData.packRate,
    formData.discountPercentage,
    formData.supplierDiscountType,
    formData.gstCategory
  ]);

  if (!isOpen || !medicine) return null;

  const handleInputChange = (field, value) => {
    if (['rack', 'batchNumber'].includes(field)) {
      if (FORBIDDEN_CHAR_REGEX.test(value)) {
        setFieldErrors((prev) => ({
          ...prev,
          [field]: 'Note: special characters comma, double quotes and single quotes are not allowed'
        }));
      } else {
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!formData.batchNumber.trim()) {
      setErrorMessage('Batch number is required');
      return;
    }
    if (!formData.noOfPackets || Number(formData.noOfPackets) <= 0) {
      setErrorMessage('No of packets must be at least 1');
      return;
    }
    if (!formData.packRate || Number(formData.packRate) < 0) {
      setErrorMessage('Pack Rate is required');
      return;
    }
    if (!formData.packMrp || Number(formData.packMrp) < 0) {
      setErrorMessage('Pack MRP is required');
      return;
    }

    // Compute formatted expiry date: Last day of chosen month/year
    const monthIndex = MONTHS.indexOf(formData.expiryMonth);
    const yearVal = parseInt(formData.expiryYear, 10);
    const lastDayOfMonth = new Date(yearVal, monthIndex + 1, 0).getDate();
    const formattedExpiry = `${yearVal}-${String(monthIndex + 1).padStart(2, '0')}-${String(lastDayOfMonth).padStart(2, '0')}`;

    const packQty = Math.max(1, Number(formData.packQuantity) || 10);
    const enteredPackRate = Number(formData.packRate) || 0;
    const unitRate = packQty > 0 ? (enteredPackRate / packQty) : enteredPackRate;
    const enteredPackMrp = Number(formData.packMrp) || 0;
    const unitMrp = packQty > 0 ? (enteredPackMrp / packQty) : enteredPackMrp;

    const configuredItem = {
      id: medicine.id || Date.now(),
      medicineId: medicine.medicineId || medicine.id,
      itemName: formData.name || medicine.brand || medicine.itemName || '',
      brand: formData.manufacturer || medicine.brand || '',
      composition: formData.composition,
      hsn: formData.hsn,
      batchNo: formData.batchNumber,
      expiryDate: formattedExpiry,
      packQuantity: liveCalculations.packQty,
      quantity: Number(formData.noOfPackets) || 1,
      freeQuantity: Number(formData.freePackets) || 0,
      mrp: liveCalculations.grossPackMrp,
      packMrp: liveCalculations.grossPackMrp,
      sellingMrp: liveCalculations.netPackMrp,
      mrpPerUnit: liveCalculations.mrpPerUnit,
      origMrpPerUnit: liveCalculations.origMrpPerUnit,
      rate: liveCalculations.packRate,
      packRate: liveCalculations.packRate,
      ratePerUnit: liveCalculations.unitRate,
      isUnitRate: false,
      discountPercent: liveCalculations.suppDiscPercent,
      discountType: formData.supplierDiscountType || '%',
      supplierDiscountType: formData.supplierDiscountType || '%',
      discountValue: Number(formData.discountPercentage) || 0,
      packMrpDiscount: Number(formData.packMrpDiscount) || 0,
      packMrpDiscountType: formData.packMrpDiscountType || '%',
      gstPercent: liveCalculations.gstRate,
      rack: formData.rack,
      thresholdQuantity: formData.thresholdQuantity,
      totalAmount: liveCalculations.totalAmount,
      taxableAmount: liveCalculations.taxableAmount,
      stockQuantity: Number(formData.stockBalance) || 0,
      availableUnits: Number(formData.stockBalance) || 0,
      availableQuantity: Number(formData.stockBalance) || 0,
      availablePacks: liveCalculations.packQty > 0 ? Math.floor((Number(formData.stockBalance) || 0) / liveCalculations.packQty) : 0,
      stockBalance: Number(formData.stockBalance) || 0
    };

    onConfirm(configuredItem);
  };

  const packUnitSingular = formData.packType || 'strip';
  const packUnitPlural = packUnitSingular.endsWith('s') ? packUnitSingular : `${packUnitSingular}s`;

  return ReactDOM.createPortal(
    <div className="mbd-modal-backdrop" onClick={onClose}>
      <div className="mbd-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="mbd-modal-header">
          <div className="mbd-header-left">
            <span className="mbd-title">{formData.name}</span>
          </div>
          <div className="mbd-header-right">
            <span className="mbd-badge-weekly">
              <FaBolt className="mbd-bolt-icon" /> Weekly {formData.weeklyVelocity}
            </span>
            <span className="mbd-badge-stock">{formData.stockBalance}</span>
            <button type="button" className="mbd-close-x-btn" onClick={onClose} title="Close">
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Scrollable Body */}
        <form onSubmit={handleSubmit} className="mbd-modal-body">
          {errorMessage && (
            <div className="mbd-alert-error">
              <FaExclamationTriangle />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* ─── SECTION 1: Item Details ─── */}
          <div className="mbd-section-card">
            <div className="mbd-section-title">Item Details</div>

            {/* Item 3-column row */}
            <div className="mbd-field-group">
              <label className="mbd-group-label">Item :</label>
              <div className="mbd-three-col-row">
                <div className="mbd-col-item">
                  <span className="mbd-col-lbl required">Name</span>
                  <span className="mbd-col-val">{formData.name || '—'}</span>
                </div>
                <div className="mbd-col-item">
                  <span className="mbd-col-lbl">Extension</span>
                  <span className="mbd-col-val">{formData.extension || '—'}</span>
                </div>
                <div className="mbd-col-item">
                  <span className="mbd-col-lbl required">Item type</span>
                  <span className="mbd-col-val">{formData.itemType || 'TABLET'}</span>
                </div>
              </div>
            </div>

            {/* Static Details rows */}
            <div className="mbd-info-pair">
              <span className="mbd-info-label required">Pack Type</span>
              <span className="mbd-info-value">{formData.packType}</span>
            </div>

            <div className="mbd-info-pair">
              <span className="mbd-info-label required">Category</span>
              <span className="mbd-info-value">{formData.category}</span>
            </div>

            <div className="mbd-info-pair">
              <span className="mbd-info-label">Schedule Type</span>
              <span className="mbd-info-value">{formData.scheduleType}</span>
            </div>

            <div className="mbd-info-pair">
              <span className="mbd-info-label">Composition</span>
              <span className="mbd-info-value">{formData.composition || '—'}</span>
            </div>

            <div className="mbd-info-pair">
              <span className="mbd-info-label">Manufacturer</span>
              <span className="mbd-info-value">{formData.manufacturer || '—'}</span>
            </div>

            <div className="mbd-info-pair">
              <span className="mbd-info-label">HSN</span>
              <span className="mbd-info-value">{formData.hsn || '—'}</span>
            </div>

            {/* GST Category with Edit button */}
            <div className="mbd-info-pair gst-row">
              <span className="mbd-info-label required">GST Category</span>
              <div className="mbd-gst-control">
                {formData.isEditingGst ? (
                  <select
                    className="mbd-select-input mini"
                    value={formData.gstCategory}
                    onChange={(e) => handleInputChange('gstCategory', e.target.value)}
                  >
                    <option value="0">0%</option>
                    <option value="5">5%</option>
                    <option value="12">12%</option>
                    <option value="18">18%</option>
                    <option value="28">28%</option>
                  </select>
                ) : (
                  <span className="mbd-info-value strong">{formData.gstCategory}%</span>
                )}
                <button
                  type="button"
                  className="mbd-btn-edit-gst"
                  onClick={() => setFormData((prev) => ({ ...prev, isEditingGst: !prev.isEditingGst }))}
                >
                  {formData.isEditingGst ? 'Save' : 'Edit'}
                </button>
              </div>
            </div>

            {/* Rack with character restrictions note */}
            <div className="mbd-form-row">
              <label className="mbd-form-label">Rack</label>
              <div className="mbd-input-col">
                <input
                  type="text"
                  className={`mbd-text-input ${fieldErrors.rack ? 'error-border' : ''}`}
                  value={formData.rack}
                  onChange={(e) => handleInputChange('rack', e.target.value)}
                />
                <span className="mbd-input-note">
                  Note: special characters comma, double quotes and single quotes are not allowed
                </span>
              </div>
            </div>

            {/* Threshold quantities */}
            <div className="mbd-form-row">
              <label className="mbd-form-label">Threshold quantity</label>
              <div className="mbd-input-col">
                <input
                  type="number"
                  min="0"
                  className="mbd-text-input"
                  value={formData.thresholdQuantity}
                  onChange={(e) => handleInputChange('thresholdQuantity', e.target.value)}
                />
              </div>
            </div>

            <div className="mbd-form-row">
              <label className="mbd-form-label">Fast Threshold value</label>
              <div className="mbd-input-col">
                <input
                  type="number"
                  min="0"
                  className="mbd-text-input"
                  value={formData.fastThresholdValue}
                  onChange={(e) => handleInputChange('fastThresholdValue', e.target.value)}
                />
              </div>
            </div>

            <div className="mbd-form-row">
              <label className="mbd-form-label">Slow Threshold Value</label>
              <div className="mbd-input-col">
                <input
                  type="number"
                  min="0"
                  className="mbd-text-input"
                  value={formData.slowThresholdValue}
                  onChange={(e) => handleInputChange('slowThresholdValue', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* ─── SECTION 2: Current Batch Details ─── */}
          <div className="mbd-section-card batch-card">
            <div className="mbd-section-title">Current batch details</div>

            {/* Pack Quantity */}
            <div className="mbd-form-row">
              <label className="mbd-form-label required">Pack Quantity</label>
              <div className="mbd-input-with-suffix">
                <input
                  type="number"
                  min="1"
                  className="mbd-text-input"
                  value={formData.packQuantity}
                  onChange={(e) => handleInputChange('packQuantity', e.target.value)}
                  disabled={isExistingBatch}
                  readOnly={isExistingBatch}
                  title={isExistingBatch ? `Pack quantity is locked to existing batch ${formData.batchNumber}` : ''}
                  required
                />
                <span className="mbd-suffix-label">per {packUnitSingular}</span>
                {isExistingBatch && (
                  <span className="mbd-locked-mini-tag" title="Fixed for existing batch">
                    🔒 Locked
                  </span>
                )}
              </div>
            </div>

            {/* No of Packets */}
            <div className="mbd-form-row">
              <label className="mbd-form-label required">No of Packets</label>
              <div className="mbd-input-with-suffix">
                <input
                  type="number"
                  min="1"
                  placeholder="Packets"
                  className="mbd-text-input"
                  value={formData.noOfPackets}
                  onChange={(e) => handleInputChange('noOfPackets', e.target.value)}
                  required
                />
                <span className="mbd-suffix-label">{packUnitPlural}</span>
              </div>
            </div>

            {/* Free packets */}
            <div className="mbd-form-row">
              <label className="mbd-form-label">Free packets</label>
              <div className="mbd-input-with-suffix">
                <input
                  type="number"
                  min="0"
                  className="mbd-text-input"
                  value={formData.freePackets}
                  onChange={(e) => handleInputChange('freePackets', e.target.value)}
                />
                <span className="mbd-suffix-label">{packUnitPlural}</span>
              </div>
            </div>

            {/* Existing Batches Selector (if any existing batches in inventory) */}
            {existingBatches.length > 0 && (
              <div className="mbd-form-row">
                <label className="mbd-form-label">
                  <FaBoxes style={{ marginRight: '6px', color: '#0ea5e9' }} /> Existing Batch
                </label>
                <div className="mbd-input-col">
                  <select
                    className="mbd-select-input"
                    value={selectedBatchId}
                    onChange={(e) => handleBatchSelect(e.target.value)}
                    style={{ fontWeight: 600 }}
                  >
                    <option value="NEW">➕ Add as New Batch</option>
                    {existingBatches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.batchNumber} (Stock: {b.stockQuantity ?? 0} units | Exp: {b.expiryDate} | MRP: ₹{b.mrp})
                      </option>
                    ))}
                  </select>
                  {isExistingBatch ? (
                    <div className="mbd-batch-lock-banner">
                      <div className="mbd-lock-text">
                        <span>🔒 Restocking into existing batch <strong>{formData.batchNumber}</strong>. Pack size, Expiry, and MRP are locked to preserve inventory integrity.</span>
                      </div>
                      <button
                        type="button"
                        className="mbd-btn-switch-new"
                        onClick={() => handleBatchSelect('NEW')}
                      >
                        ➕ Switch to New Batch
                      </button>
                    </div>
                  ) : (
                    <span className="mbd-input-note" style={{ color: '#0ea5e9', fontWeight: 500 }}>
                      Creating a brand new batch entry for this medicine in inventory
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Batch number with note */}
            <div className="mbd-form-row">
              <label className="mbd-form-label required">Batch number</label>
              <div className="mbd-input-col">
                <input
                  type="text"
                  className={`mbd-text-input ${fieldErrors.batchNumber ? 'error-border' : ''}`}
                  value={formData.batchNumber}
                  onChange={(e) => handleInputChange('batchNumber', e.target.value)}
                  readOnly={isExistingBatch}
                  disabled={isExistingBatch}
                  title={isExistingBatch ? `Locked to existing batch ${formData.batchNumber}` : ''}
                  required
                />
                <span className="mbd-input-note">
                  {isExistingBatch
                    ? `Locked to existing batch "${formData.batchNumber}". Click 'Switch to New Batch' above to enter a new batch.`
                    : 'Note: special characters comma, double quotes and single quotes are not allowed'}
                </span>
              </div>
            </div>

            {/* Expiry Date (Month + Year dropdowns) */}
            <div className="mbd-form-row">
              <label className="mbd-form-label required">Expiry Date</label>
              <div className="mbd-expiry-row">
                <select
                  className="mbd-select-input"
                  value={formData.expiryMonth}
                  onChange={(e) => handleInputChange('expiryMonth', e.target.value)}
                  disabled={isExistingBatch}
                  title={isExistingBatch ? 'Expiry date is locked for existing batch' : ''}
                >
                  {MONTHS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <select
                  className="mbd-select-input"
                  value={formData.expiryYear}
                  onChange={(e) => handleInputChange('expiryYear', e.target.value)}
                  disabled={isExistingBatch}
                  title={isExistingBatch ? 'Expiry date is locked for existing batch' : ''}
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                {isExistingBatch && (
                  <span className="mbd-locked-mini-tag" title="Fixed for existing batch">
                    🔒 Locked
                  </span>
                )}
              </div>
            </div>

            {/* Pack Rate */}
            <div className="mbd-form-row">
              <label className="mbd-form-label required">
                Pack Rate
              </label>
              <div className="mbd-input-col">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className="mbd-text-input"
                  value={formData.packRate}
                  onChange={(e) => handleInputChange('packRate', e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Pack MRP with live Selling MRP & MRP per Unit */}
            <div className="mbd-form-row">
              <label className="mbd-form-label required">
                Pack Mrp
              </label>
              <div className="mbd-mrp-col">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="mbd-text-input"
                  value={formData.packMrp}
                  onChange={(e) => handleInputChange('packMrp', e.target.value)}
                  readOnly={isExistingBatch}
                  disabled={isExistingBatch}
                  title={isExistingBatch ? `MRP ₹${formData.packMrp} is fixed for existing batch ${formData.batchNumber}` : ''}
                  required
                />
                {isExistingBatch && (
                  <span className="mbd-locked-chip" title="Fixed for existing batch">
                    🔒 Batch MRP Locked
                  </span>
                )}
              </div>
            </div>

            {/* Discount on MRP */}
            <div className="mbd-form-row">
              <label className="mbd-form-label">
                Discount on MRP
              </label>
              <div className="mbd-input-col">
                <div className="mbd-discount-input-group">
                  <input
                    type="number"
                    step={formData.packMrpDiscountType === '₹' ? '0.01' : '0.1'}
                    min="0"
                    max={formData.packMrpDiscountType === '%' ? '100' : undefined}
                    placeholder={formData.packMrpDiscountType === '%' ? '0 %' : '₹ 0.00'}
                    className="mbd-text-input"
                    value={formData.packMrpDiscount}
                    onChange={(e) => handleInputChange('packMrpDiscount', e.target.value)}
                  />
                  <div className="mbd-unit-toggle">
                    <button
                      type="button"
                      className={`mbd-unit-btn ${formData.packMrpDiscountType === '%' ? 'active' : ''}`}
                      onClick={() => handleInputChange('packMrpDiscountType', '%')}
                      title="Percentage Discount"
                    >
                      %
                    </button>
                    <button
                      type="button"
                      className={`mbd-unit-btn ${formData.packMrpDiscountType === '₹' ? 'active' : ''}`}
                      onClick={() => handleInputChange('packMrpDiscountType', '₹')}
                      title="Rupees Discount"
                    >
                      ₹
                    </button>
                  </div>
                </div>

                {/* Live Calculated MRP per Unit & Selling MRP badges */}
                <div className="mbd-live-calc-badge-row">
                  <span className="mbd-calc-pill mrp-pill">
                    Selling MRP: <strong>₹ {liveCalculations.netPackMrp.toFixed(2)}</strong>
                  </span>
                  <span className="mbd-calc-pill unit-pill">
                    MRP / Unit: <strong>₹ {liveCalculations.mrpPerUnit.toFixed(2)}</strong>
                    {liveCalculations.mrpDiscountAmount > 0 && (
                      <span className="mbd-calc-orig" title="Original MRP per unit without discount">
                        ₹ {liveCalculations.origMrpPerUnit.toFixed(2)}
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Discount given by supplier */}
            <div className="mbd-form-row">
              <label className="mbd-form-label">
                Discount given by supplier
              </label>
              <div className="mbd-input-col">
                <div className="mbd-discount-input-group">
                  <input
                    type="number"
                    step={formData.supplierDiscountType === '₹' ? '0.01' : '0.1'}
                    min="0"
                    max={formData.supplierDiscountType === '%' ? '100' : undefined}
                    placeholder={formData.supplierDiscountType === '%' ? '0 %' : '₹ 0.00'}
                    className="mbd-text-input"
                    value={formData.discountPercentage}
                    onChange={(e) => handleInputChange('discountPercentage', e.target.value)}
                  />
                  <div className="mbd-unit-toggle">
                    <button
                      type="button"
                      className={`mbd-unit-btn ${formData.supplierDiscountType === '%' ? 'active' : ''}`}
                      onClick={() => handleInputChange('supplierDiscountType', '%')}
                      title="Percentage Discount"
                    >
                      %
                    </button>
                    <button
                      type="button"
                      className={`mbd-unit-btn ${formData.supplierDiscountType === '₹' ? 'active' : ''}`}
                      onClick={() => handleInputChange('supplierDiscountType', '₹')}
                      title="Rupees Discount"
                    >
                      ₹
                    </button>
                  </div>
                </div>

                {/* Live Amount Breakdown Box based on entered discount */}
                <div className="mbd-batch-summary-box">
                  <div className="mbd-summary-line">
                    <span className="lbl">Base Amount ({formData.noOfPackets || 1} pk × ₹ {(Number(formData.packRate) || 0).toFixed(2)}):</span>
                    <span className="val">₹ {liveCalculations.basePurchaseAmount.toFixed(2)}</span>
                  </div>
                  {liveCalculations.supplierDiscountAmount > 0 && (
                    <div className="mbd-summary-line discount">
                      <span className="lbl">
                        Supplier Discount ({formData.supplierDiscountType === '₹' ? `₹ ${formData.discountPercentage}/pk` : `${formData.discountPercentage}%`}):
                      </span>
                      <span className="val">- ₹ {liveCalculations.supplierDiscountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="mbd-summary-line">
                    <span className="lbl">Taxable Amount:</span>
                    <span className="val">₹ {liveCalculations.taxableAmount.toFixed(2)}</span>
                  </div>
                  <div className="mbd-summary-line">
                    <span className="lbl">GST ({liveCalculations.gstRate}%):</span>
                    <span className="val">+ ₹ {liveCalculations.gstAmount.toFixed(2)}</span>
                  </div>
                  <div className="mbd-summary-total-line">
                    <span className="lbl">Total Amount:</span>
                    <span className="val highlight">₹ {liveCalculations.totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer inside form */}
          <div className="mbd-modal-footer">
            <button type="submit" className="mbd-btn-submit">
              Submit
            </button>
            <button type="button" className="mbd-btn-close" onClick={onClose}>
              Close
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default MedicineBatchDetailModal;
